import { useEffect, useRef } from 'react';
import { Button, Input } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { useTeamContext } from '../../../Team.context';
import { isUndefined } from 'lodash';
import { ROLE_OPTIONS } from '../../../Team.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useSubscriptionAnalytics } from 'src/hooks/analytics/subscription/useSubscriptionAnalytics.hook';
import { SUBSCRIPTION_EVENTS } from 'src/hooks/analytics/subscription/subscriptionAnalytics.constants';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import InputPassword from '../../../../../../../components/InputPassword/InputPassword.component';
import { eUserRole } from '../../../../../../../providers/UserProvider/User.enums';
import { useWorkspaceContext } from '../../../../../../../providers/WorkspaceProvider/Workspace.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useSettingsContext } from 'src/pages/Settings/Settings.context';
import { SettingsTabs } from 'src/pages/Settings/Settings.enum';
import SeatCostNotice from './SeatCostNotice.component';
import SeatCardCapture from './SeatCardCapture.component';
import {
  useSeatPaymentMethod,
  eSeatPaymentState,
} from 'src/providers/SubscriptionProvider/useSeatPaymentMethod.hook';
import { eSeatFlowOutcome } from '../../../Team.enums';
import { resolveSeatBillingMode, formatSeatAmount } from './SeatCostNotice.functions';
import { eSeatBillingMode } from './SeatCostNotice.types';

export default function AddWorkspaceUserForm() {
  const { t } = useTranslation('settings/team');
  const { trackEvent } = useAnalytics();
  const { trackSubscriptionEvent } = useSubscriptionAnalytics();
  const {
    addUserFormik,
    addingUser,
    pipesSelectionOptions,
    seatPurchaseRequired,
    seatFlowOutcome,
    seatFlowMessage,
    seatPurchase,
    isCardCaptureOpen,
    openCardCapture,
  } = useTeamContext();
  const { workspaceInfo } = useWorkspaceContext();
  const { subscription } = useSubscriptionContext();
  const { setSelectedTab } = useSettingsContext();

  // The SHARED instance — the same one that performs the charge. Mounting a second
  // copy here is what let the button's readiness disagree with the purchase's.
  const { quote, amountDueToday, isLoadingAmountDueToday, isReady } = seatPurchase;

  // Once the seat is bought, the price disappears from this form. A priced control
  // sitting next to "we could not invite them" reads as a second charge, and the retry
  // must invite only (R5).
  const seatAlreadyPaid =
    seatFlowOutcome === eSeatFlowOutcome.SEAT_PURCHASED_INVITE_FAILED;
  const showSeatCost = seatPurchaseRequired && !seatAlreadyPaid;

  // U5 — fired in the background when the flow opens at the limit, so it resolves
  // while the customer types. The form is never blocked on it; only submit waits.
  const { state: paymentState, isResolved: isPaymentResolved } =
    useSeatPaymentMethod({ enabled: showSeatCost });

  // Two triggers, one block: the proactive lookup below, and the customer choosing
  // "use a different card" after a decline (U6). Never auto-opened by a decline.
  const isCardCaptureVisible =
    showSeatCost &&
    (isCardCaptureOpen || paymentState === eSeatPaymentState.NEEDS_CARD);

  const billingModeForAnalytics = resolveSeatBillingMode({
    status: subscription?.status,
    collectionMethod: subscription?.subscriptionCollectionMethod,
  });

  // R18 — the disclosure is reported once per flow, and only once it actually says
  // something: emitting while the price is still resolving would count views of a
  // notice the customer never read.
  const disclosureReportedRef = useRef(false);
  useEffect(() => {
    if (!showSeatCost || !isReady || disclosureReportedRef.current) return;
    disclosureReportedRef.current = true;
    trackSubscriptionEvent(SUBSCRIPTION_EVENTS.SEAT_DISCLOSURE_SHOWN, {
      quote_available: quote.available,
      billing_mode: billingModeForAnalytics,
    });
  }, [showSeatCost, isReady, quote.available, billingModeForAnalytics, trackSubscriptionEvent]);

  useEffect(() => {
    if (!seatPurchaseRequired) disclosureReportedRef.current = false;
  }, [seatPurchaseRequired]);

  const billingMode = resolveSeatBillingMode({
    status: subscription?.status,
    collectionMethod: subscription?.subscriptionCollectionMethod,
  });

  // R3 — the control that takes the money always carries an amount: what is due
  // today when that is known, otherwise the recurring per-seat figure.
  const submitAmount = !showSeatCost
    ? null
    : amountDueToday.known
      ? formatSeatAmount(amountDueToday.amountCents, amountDueToday.currency)
      : quote.available
        ? formatSeatAmount(quote.recurringAmountCents, quote.currency)
        : null;

  // Every disabled state says why. Silence on a money control reads as a broken page.
  // One expression decides BOTH whether submit is blocked and what the visible
  // reason says, so the form can never show "looking up the price" beside an enabled
  // button. `isLoadingAmountDueToday` is deliberately absent: R3 keeps submit
  // actionable on the recurring amount while the today-figure resolves.
  const seatBlockReason = !showSeatCost
    ? null
    : !isReady
      ? t('page.seatCost.disabled.price')
      : billingMode === eSeatBillingMode.UNKNOWN
        ? t('page.seatCost.noQuote.billingUnknown')
      : !quote.available
        ? t('page.seatCost.disabled.noQuote')
        : // A legacy plan quotes a real price but has no product to buy.
          !quote.purchasable
          ? t('page.seatCost.noQuote.subscription')
          : // Gate on the lookup having RESOLVED rather than guessing which rail this
            // workspace is on. An unresolved lookup is not "no card".
            !isPaymentResolved
            ? t('page.seatCost.disabled.payment')
            : null;

  const isSeatBlocked = showSeatCost && seatBlockReason !== null;

  useEffect(() => {
    if (isUndefined(addUserFormik)) {
      return;
    }

    if (addUserFormik?.values.role.label === eUserRole.ADMIN) {
      addUserFormik?.setFieldValue('pipes', pipesSelectionOptions);
    } else {
      addUserFormik?.setFieldValue('pipes', []);
    }
  }, [addUserFormik?.values?.role]);

  if (isUndefined(addUserFormik)) {
    return <></>;
  }

  return (
    <form
      onSubmit={(event) => {
        if (isSeatBlocked) {
          event.preventDefault();
          return;
        }
        addUserFormik.handleSubmit(event);
      }}
      noValidate
      className="TeamSection__Modal__Form"
    >
      <div className="TeamSection__Modal__Form__Container">
        <div className="TeamSection__Modal__Form__New__User">
          <span className="text-lg">
            {t('page.form.label.modal-name.text')}
          </span>
          <Input
            id="firstName"
            name="firstName"
            data-tour="invite-name-input"
            value={addUserFormik.values.firstName}
            onChange={addUserFormik.handleChange}
            onBlur={addUserFormik.handleBlur}
            autoComplete="off"
            className="w-full"
            placeholder="Nombre del usuario"
            type="text"
          />
          {addUserFormik.touched.firstName && addUserFormik.errors.firstName ? (
            <div className="ProfileForm__errorMessage">
              {addUserFormik.errors.firstName}
            </div>
          ) : null}
          <span className="text-lg">
            {t('page.form.label.modal-email.text')}
          </span>
          <Input
            id="email"
            name="email"
            data-tour="invite-email-input"
            value={addUserFormik.values.email}
            onChange={addUserFormik.handleChange}
            onBlur={addUserFormik.handleBlur}
            autoComplete="off"
            className="w-full"
            placeholder="Correo del usuario"
            type="email"
          />
          {addUserFormik.touched.email && addUserFormik.errors.email ? (
            <div className="ProfileForm__errorMessage">
              {addUserFormik.errors.email}
            </div>
          ) : addUserFormik.touched.email && !addUserFormik.errors.email ? (
            <div className="text-xs">
              {t('page.form.label.modal-email.helperText')}
            </div>
          ) : null}
          <div className="flex items-center gap-1">
            <span className="text-lg">{t('page.form.label.rol.text')}</span>
            <div className="relative group">
              <InformationCircleIcon className="TeamSection__Badge__custom__Icon" />
              <div className="TeamSection__Badge__custom">
                <div className="space-y-2">
                  <p className="TeamSection__Badge__custom__paragraph">
                    <b>Owner:</b> {t('page.tooltipMessage.ownerRole')}
                  </p>
                  <p className="TeamSection__Badge__custom__paragraph">
                    <b>Admin:</b> {t('page.tooltipMessage.adminRole')}
                  </p>
                  <p className="TeamSection__Badge__custom__paragraph">
                    <b>Seller:</b> {t('page.tooltipMessage.sellerRole')}
                  </p>
                </div>
                <div className="TeamSection__Badge__custom__arrow"></div>
              </div>
            </div>
          </div>
          {/* react-select keeps unknown props off the DOM: the tour anchor wraps it. */}
          <div data-tour="invite-role-select">
            <Select
              options={ROLE_OPTIONS}
              id="role"
              name="role"
              value={addUserFormik.values.role}
              onChange={(value) => addUserFormik.setFieldValue('role', value)}
              onBlur={addUserFormik.handleBlur}
            />
          </div>
          {addUserFormik.touched.role && addUserFormik.errors.role ? (
            <div className="ProfileForm__errorMessage">
              {addUserFormik.errors.role.value}
            </div>
          ) : null}
          <div className="text-xs">
            El rol define los permisos y vistas disponibles.
          </div>
        </div>
      </div>
      {!workspaceInfo?.userManagementV2 && (
        <div>
          <div className="TeamSection__Modal__FieldContainer">
            <label className="text-lg">{t('page.form.label.pipes.text')}</label>
            <Select
              options={pipesSelectionOptions}
              id="pipes"
              name="pipes"
              isDisabled={addUserFormik.values.role.label === eUserRole.ADMIN}
              value={addUserFormik.values.pipes}
              onChange={(value) => addUserFormik.setFieldValue('pipes', value)}
              onBlur={addUserFormik.handleBlur}
              isMulti
            />
            <span className="text-sm text-primary">
              {addUserFormik.values.role.label === eUserRole.ADMIN
                ? 'Recuerda que los usuarios con rol ADMIN tienen acceso a todos los embudos.'
                : null}
            </span>

            {addUserFormik.touched.pipes &&
            addUserFormik.values?.pipes &&
            addUserFormik.values.pipes.length <= 0 ? (
              <div className="ProfileForm__errorMessage">
                El usuario debe tener acceso a al menos un embudo
              </div>
            ) : null}
          </div>
          <div className="TeamSection__Modal__FieldContainer">
            <div className="TeamSection__Modal__FieldContainerInputs">
              <label htmlFor="inputPassword" className="SecurityForm__label">
                <span className="text-lg">
                  {t('page.form.label.password.text')}
                </span>
              </label>
              <InputPassword
                id="password"
                name="password"
                value={addUserFormik.values.password}
                onChange={addUserFormik.handleChange}
                onBlur={addUserFormik.handleBlur}
                placeholder="Ingresa nueva contraseña"
              />
              {addUserFormik.touched.password &&
              addUserFormik.errors.password ? (
                <div className="ProfileForm__errorMessage">
                  {addUserFormik.errors.password}
                </div>
              ) : null}
            </div>
            <div className="TeamSection__Modal__FieldContainerInputs">
              <label htmlFor="inputPassword" className="SecurityForm__label">
                <span className="text-lg">
                  {t('page.form.label.confirmPassword.text')}
                </span>
              </label>
              <InputPassword
                id="confirmedPassword"
                name="confirmedPassword"
                value={addUserFormik.values.confirmedPassword}
                onChange={addUserFormik.handleChange}
                onBlur={addUserFormik.handleBlur}
                placeholder="Confirma la contraseña"
              />
              {addUserFormik.touched.confirmedPassword &&
              addUserFormik.errors.confirmedPassword ? (
                <div className="ProfileForm__errorMessage">
                  {addUserFormik.errors.confirmedPassword}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {showSeatCost && (
        <>
          {/* A divider keeps identity and money legible as two concerns as the form
              grows, rather than one long column of fields. */}
          <hr className="TeamSection__Modal__Form__Divider" />
          <SeatCostNotice
            quote={quote}
            amountDueToday={amountDueToday}
            isLoadingAmountDueToday={isLoadingAmountDueToday}
            billingMode={billingMode}
            onGoToSubscription={() => setSelectedTab(SettingsTabs.SUBSCRIPTION)}
          />
          {/* Directly beneath the notice and above the submit control, inside this
              same form — an inline reveal, not the nested modal the other payment
              surfaces use. Never rendered for an invoice-billed workspace. */}
          <SeatCardCapture isOpen={isCardCaptureVisible} />
        </>
      )}
      {seatAlreadyPaid && (
        <section
          className="TeamSection__Modal__Form__SeatOutcome"
          data-testid="seat-invite-failed-notice"
          aria-live="assertive"
        >
          <p>{t('page.seatCost.outcome.invitePending')}</p>
          {seatFlowMessage && <p>{seatFlowMessage}</p>}
        </section>
      )}

      {seatFlowOutcome === eSeatFlowOutcome.DECLINED && (
        <section
          className="TeamSection__Modal__Form__SeatOutcome"
          data-testid="seat-declined-notice"
          aria-live="assertive"
        >
          <p>{seatFlowMessage ?? t('page.seatCost.outcome.declined')}</p>
          {/* Card capture is NOT forced open here: a decline can be a transient
              hold on a perfectly good card, and presuming the card is at fault
              would make the customer re-enter details they may not need to. */}
          <Button
            type="button"
            size="sm"
            color="ghost"
            data-testid="seat-declined-use-another-card"
            onClick={openCardCapture}
          >
            {t('page.seatCost.outcome.useAnotherCard')}
          </Button>
        </section>
      )}

      <div className="TeamSection__Modal__Form__ButtonContainer">
        {seatBlockReason && (
          <p
            className="TeamSection__Modal__Form__DisabledReason"
            data-testid="seat-submit-disabled-reason"
            aria-live="polite"
          >
            {seatBlockReason}
          </p>
        )}
        <Button
          type="submit"
          className="ProfileForm__submitButton"
          data-tour="invite-submit-button"
          size="md"
          color="primary"
          loading={addingUser}
          data-testid="add-user-submit-button"
          disabled={!addUserFormik.isValid || isSeatBlocked}
          onClick={() => {
            if (isSeatBlocked) return;
            addUserFormik.handleSubmit();
            trackEvent('front.settings.team.new_member.save_btn.click', {
              displayName: 'Agregar miembro del equipo',
              description: 'Agregar un usuario nuevo con éxito',
            });
          }}
        >
          {/* The amount is announced when it changes, so it is not a silent swap on
              the control the customer is about to press. */}
          <span aria-live="polite">
            {seatAlreadyPaid
              ? t('page.seatCost.outcome.retryInvite')
              : submitAmount
                ? t('page.form.button.submit.withAmount', {
                    amount: submitAmount,
                  })
                : t('page.form.button.submit.text')}
          </span>
        </Button>
      </div>
    </form>
  );
}
